import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Phase 22 — Template Gallery for the Sales Page.
 *
 * Pulls /api/templates/gallery (75 templates: cuisines, cafes, bakeries,
 * hotels, rooftop bars, speakeasies, nightclubs, alfresco, etc.) and
 * displays them as a glossy, lazy-loaded grid. Each card uses the
 * template's accent colour for a thin top stripe so the variety reads
 * even at thumbnail size.
 */
export default function SalesTemplateGallery({ heading, subheading }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/templates/gallery")
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  // Only filter out genuine Lorem Ipsum placeholder rows (label or tagline
  // contains "Lorem Ipsum" or "Hustle Local"). The full named/venue/generic
  // gallery (70+ cuisines) stays.
  const cleaned = items.filter((t) => {
    const blob = `${t.label || ""} ${t.tagline || ""}`.toLowerCase();
    return !blob.includes("lorem ipsum") && !blob.includes("hustle local");
  });
  const categories = Array.from(new Set(cleaned.map((t) => t.category).filter(Boolean)));
  const filtered = filter ? cleaned.filter((t) => t.category === filter) : cleaned;
  const visibleCategories = categories;

  return (
    <section
      data-testid="sales-template-gallery"
      className="bg-[#FBF7EE] py-16 md:py-20 border-y border-black/8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.32em] text-[#C05746] font-medium">
            Template library · {cleaned.length}
          </p>
          <h2 className="mt-3 font-serif-display text-4xl md:text-5xl text-[#1A1410]">
            {heading || `${cleaned.length} templates. Every cuisine. Every vibe.`}
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-[#5C625E]">
            {subheading || "From a single-page café to a 12-section hotel restaurant. Pick one, change the photos, you're in business."}
          </p>
        </div>

        {categories.length > 1 && (
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setFilter("")}
              data-testid="template-filter-all"
              className={`text-xs px-3 py-1.5 rounded-full border ${
                filter === ""
                  ? "bg-[#1A1410] text-white border-[#1A1410]"
                  : "bg-white text-[#1A1410] border-black/15 hover:bg-black/5"
              }`}
            >
              All ({cleaned.length})
            </button>
            {visibleCategories.map((c) => {
              const n = cleaned.filter((t) => t.category === c).length;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  data-testid={`template-filter-${c}`}
                  className={`text-xs px-3 py-1.5 rounded-full border capitalize ${
                    filter === c
                      ? "bg-[#1A1410] text-white border-[#1A1410]"
                      : "bg-white text-[#1A1410] border-black/15 hover:bg-black/5"
                  }`}
                >
                  {c} ({n})
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <p className="mt-10 text-center text-sm text-[#5C625E]">Loading the gallery…</p>
        ) : (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map((t) => (
              <article
                key={t.code}
                data-testid={`template-card-${t.code}`}
                className="group relative rounded-2xl overflow-hidden bg-white border border-black/8 shadow-sm hover:shadow-xl transition-shadow"
              >
                <div
                  className="h-1.5"
                  style={{ backgroundColor: t.accent || "#C05746" }}
                />
                <div
                  className="aspect-[4/5] bg-cover bg-center bg-[#F6EFE0]"
                  style={t.preview_image ? { backgroundImage: `url("${t.preview_image}")` } : {}}
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white">
                  <p className="text-[10px] uppercase tracking-[0.22em] opacity-80">
                    {t.category || "Restaurant"}
                  </p>
                  <h3 className="mt-1 font-serif-display text-xl leading-tight">
                    {t.label}
                  </h3>
                  {t.tagline && (
                    <p className="mt-1 text-xs italic opacity-85 line-clamp-1">{t.tagline}</p>
                  )}
                </div>
              </article>
            ))}

            {/* "Custom build" card — sits at the end as a placeholder/promise. */}
            <article
              data-testid="template-card-custom"
              className="group relative rounded-2xl overflow-hidden border-2 border-dashed border-[#C05746]/40 bg-white hover:border-[#C05746] hover:shadow-xl transition-all"
            >
              <div className="h-1.5 bg-[#C05746]" />
              <div
                className="aspect-[4/5] bg-cover bg-center bg-[#F6EFE0]"
                style={{
                  backgroundImage:
                    'url("https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=70")',
                }}
              />
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 via-black/55 to-transparent text-white">
                <p className="text-[10px] uppercase tracking-[0.22em] opacity-90 text-[#F5C84B]">
                  Custom build · 7 days
                </p>
                <h3 className="mt-1 font-serif-display text-xl leading-tight">
                  Don't see your cuisine?
                </h3>
                <p className="mt-1 text-xs italic opacity-90 leading-snug">
                  We'll build it for you — branded, photographed and live in a week.
                </p>
              </div>
            </article>
          </div>
        )}
      </div>
    </section>
  );
}
